// src/RecipeDB/AddToRecetarioModal.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../Auth/AuthContext';
import { useToast } from '../context/ToastContext';
import {
  getRecetariosOfUser,
  createRecetario,
  addRecetaToRecetario,
  removeRecetaFromRecetario,
} from './recetarioService';
import getRecipeId from '../utils/getRecipeId';
import './AddToRecetarioModal.css';

/**
 * Modal para añadir/quitar una receta de los recetarios del usuario.
 *
 * Props:
 *  - recipe: objeto receta (necesita .id y opcionalmente .imagen)
 *  - onClose: callback para cerrar
 */
const AddToRecetarioModal = ({ recipe, onClose, onSaved }) => {
  const { userId } = useAuth();
  const { showToast } = useToast();

  const [recetarios, setRecetarios]       = useState([]);
  const [checked, setChecked]             = useState(new Set()); // IDs de recetarios que ya contienen la receta
  const [original, setOriginal]           = useState(new Set()); // estado inicial para calcular cambios
  const [creating, setCreating]           = useState(false);
  const [newNombre, setNewNombre]         = useState('');
  const [newDesc, setNewDesc]             = useState('');
  const [saving, setSaving]               = useState(false);

  const recetaId = getRecipeId(recipe);
  const coverUrl = recipe?.imagen || (recipe?.imagenes?.[0]) || null;

  useEffect(() => {
    if (!userId) return;
    getRecetariosOfUser(userId).then(list => {
      setRecetarios(list);
      const alreadyIn = new Set(
        list.filter(r => r.recetaIds?.includes(recetaId)).map(r => r.id)
      );
      setChecked(new Set(alreadyIn));
      setOriginal(new Set(alreadyIn));
    });
  }, [userId, recetaId]);

  const toggle = (recetarioId) => {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(recetarioId) ? next.delete(recetarioId) : next.add(recetarioId);
      return next;
    });
  };

  const handleCreateAndAdd = async () => {
    if (!newNombre.trim()) return;
    if (!recetaId) {
      showToast('No se pudo identificar la receta', 'error');
      return;
    }
    setSaving(true);
    try {
      const id = await createRecetario(userId, { nombre: newNombre, descripcion: newDesc });
      await addRecetaToRecetario(id, recetaId, coverUrl);
      showToast(`Recetario "${newNombre}" creado y receta añadida 🎉`, 'success');
      onSaved?.({ created: true, recetarioId: id });
      onClose();
    } catch {
      showToast('Error al crear el recetario', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (creating) { await handleCreateAndAdd(); return; }
    if (!recetaId) {
      showToast('No se pudo identificar la receta', 'error');
      return;
    }
    setSaving(true);
    try {
      const toAdd    = [...checked].filter(id => !original.has(id));
      const toRemove = [...original].filter(id => !checked.has(id));

      await Promise.all([
        ...toAdd.map(id    => addRecetaToRecetario(id, recetaId, coverUrl)),
        ...toRemove.map(id => removeRecetaFromRecetario(id, recetaId)),
      ]);

      const changed = toAdd.length + toRemove.length;
      if (changed > 0) showToast('Recetarios actualizados ✓', 'success');
      onSaved?.({ created: false, changed });
      onClose();
    } catch {
      showToast('Error al actualizar los recetarios', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="recetario-modal-overlay" onClick={onClose}>
      <div className="recetario-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="recetario-modal__header">
          <h3>📚 Añadir a recetario</h3>
          <button className="recetario-modal__close" onClick={onClose}>✕</button>
        </div>

        {/* Lista de recetarios */}
        {!creating && (
          <div className="recetario-modal__list">
            {recetarios.length === 0 ? (
              <p className="recetario-modal__empty">
                Todavía no tienes recetarios.<br />¡Crea el primero!
              </p>
            ) : (
              recetarios.map(r => (
                <div key={r.id} className="recetario-item" onClick={() => toggle(r.id)}>
                  <div className={`recetario-item__check${checked.has(r.id) ? ' checked' : ''}`}>
                    {checked.has(r.id) && '✓'}
                  </div>
                  <span className="recetario-item__name">{r.nombre}</span>
                  <span className="recetario-item__count">{r.recetaIds?.length ?? 0} recetas</span>
                </div>
              ))
            )}
          </div>
        )}

        {/* Formulario de creación inline */}
        {creating && (
          <div className="recetario-modal__list">
            <div className="recetario-modal__create" style={{ padding: '16px 20px' }}>
              <input
                type="text"
                placeholder="Nombre del recetario *"
                value={newNombre}
                onChange={e => setNewNombre(e.target.value)}
                autoFocus
                maxLength={60}
              />
              <textarea
                placeholder="Descripción (opcional)"
                value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
                rows={2}
                maxLength={200}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="recetario-modal__footer">
          <button
            className="recetario-modal__save"
            onClick={handleSave}
            disabled={saving || (creating && !newNombre.trim())}
          >
            {saving ? 'Guardando…' : creating ? 'Crear y añadir' : 'Guardar'}
          </button>
          <button
            className="recetario-modal__new"
            onClick={() => setCreating(v => !v)}
          >
            {creating ? '← Volver a mis recetarios' : '+ Crear nuevo recetario'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToRecetarioModal;
